import { useState, useEffect } from "react";
import logo from "../../assets/logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import SignUp from "./SignUp";
import {
  createUser,
  resendVerificationToken,
  verifyUser,
} from "../../api/user";

const CreateAccount = () => {
  const formSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]/,
        "Password must contain at least one letter and one number"
      )
      .required("Password is required"),
    repeatPassword: Yup.string()
      .required("Please retype your password")
      .oneOf([Yup.ref("password")], "These passwords don't match up"),
    receiveEmails: Yup.boolean().required(),
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    repeatPassword: "",
    receiveEmails: false,
  });

  const [errors, setErrors] = useState({});
  const [googleError, setGoogleError] = useState("");

  useEffect(() => {
    if (location.state?.email) {
      setFormData((prev) => ({ ...prev, email: location.state.email }));
      setCurrentStep(2);
    }
  }, [location.state]);

  const nextStep = () => setCurrentStep(currentStep + 1);

  const handleChange = (input: any) => (e: any) => {
    const { value } = e.target;
    const updatedFormData = { ...formData, [input]: value };

    setFormData(updatedFormData);

    formSchema
      .validateAt(input, updatedFormData)
      .then(() => setErrors((prev) => ({ ...prev, [input]: "" })))
      .catch((err) => setErrors((prev) => ({ ...prev, [input]: err.message })));

    if (input === "password") {
      formSchema
        .validateAt("repeatPassword", updatedFormData)
        .then(() => setErrors((prev) => ({ ...prev, repeatPassword: "" })))
        .catch((err) =>
          setErrors((prev) => ({ ...prev, repeatPassword: err.message }))
        );
    }
  };

  const validateForm = async () => {
    try {
      await formSchema.validate(formData, { abortEarly: false });
      setErrors({});
      const userFullName = `${formData.firstName} ${formData.lastName}`;
      await createUser(
        formData.email,
        formData.password,
        userFullName,
        formData.receiveEmails
      );
      nextStep();
    } catch (err: any) {
      console.log(err);
      if (err.status === 409) {
        setErrors((prev) => ({ ...prev, emailExists: true }));
      } else {
        const formattedErrors = err.inner.reduce(
          (acc: Record<string, string>, currentError: any) => {
            acc[currentError.path] = currentError.message;
            return acc;
          },
          {}
        );
        setErrors(formattedErrors);
      }
    }
  };

  switch (currentStep) {
    case 1:
      return (
        <SignUp
          nextStep={nextStep}
          handleChange={handleChange}
          formData={formData}
          validateForm={validateForm}
          errors={errors}
          setGoogleError={setGoogleError}
        />
      );
    case 2:
      return <ConfirmationStep formData={formData} nextStep={nextStep} />;
    case 3:
      return <EntranceToLogin />;
  }
};

interface ConfirmationStepProps {
  formData: any;
  nextStep: any;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  formData,
  nextStep,
}: {
  formData: any;
  nextStep: any;
}) => {
  const [userInput, setUserInput] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    try {
      const verify = await verifyUser(formData.email, userInput);
      if (verify === "approved") {
        nextStep();
      } else {
        setError("Confirmation code doesn't match. Please try again.");
      }
    } catch (error: any) {
      setError("Confirmation code doesn't match. Please try again.");
    }
  };

  const handleInputChange = (e: any) => {
    setUserInput(e.target.value);
    if (error) setError("");
  };

  const [cooldown, setCooldown] = useState(false);

  const resendCode = async () => {
    const email = formData.email;
    try {
      await resendVerificationToken(email);
      setCooldown(true);
      setTimeout(() => {
        setCooldown(false);
      }, 20000);
    } catch (error: any) {
      setError(error);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center h-screen relative">
        <div className="flex flex-col items-center bg-grey rounded-3xl p-8 w-[370px] md:w-[500px] md:m-4">
          <img src={logo} className="h-8 mb-8" alt="Logo" />
          <h1 className="text-md md:text-lg">
            We've sent you a confirmation code
          </h1>
          <h2 className="text-sm md:text-md mb-4 mt-2">
            Please enter it below
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col w-full px-4 ">
              <input
                className="bg-lightgrey rounded-3xl px-4 py-2 mt-4"
                type="text"
                placeholder="Verification Code"
                onChange={handleInputChange}
                value={userInput}
              />
              {error && <div className="text-red text-xs mt-2">{error}</div>}

              <button
                className={`rounded-3xl text-black px-4 py-2 mt-4
                ${
                  userInput.length >= 6
                    ? "bg-yellow hover:brightness-90"
                    : "bg-white"
                }`}
                type="submit"
                disabled={userInput.length < 6}
              >
                Submit
              </button>
            </div>
          </form>
        </div>
        <button
          type="button"
          className={`rounded-3xl text-yellow px-4 py-2 hover:brightness-90 ${
            cooldown ? "cursor-not-allowed" : ""
          }`}
          onClick={resendCode}
          disabled={cooldown}
        >
          Resend Verification Code
        </button>
        {cooldown && (
          <span className="text-xs text-white text-opacity-60">
            Please wait 20s before requesting another code
          </span>
        )}
      </div>
    </>
  );
};

const EntranceToLogin = () => {
  const navigate = useNavigate();
  return (
    <>
      <div className="flex flex-col items-center justify-center h-screen relative">
        <div className="flex flex-col items-center bg-grey rounded-3xl p-8 w-[370px] md:w-[500px] md:m-4">
          <img src={logo} className="h-8 mb-8" alt="Logo" />
          <h1 className="text-xl">You're really you 😄</h1>

          <h2 className="text-base my-6">Click below to get started</h2>
          <button
            className="bg-yellow text-black text-black rounded-3xl p-2 mt-4 w-64 md:w-80 hover:brightness-90"
            onClick={() => navigate(`/login`)}
          >
            Login
          </button>
        </div>
      </div>
    </>
  );
};

export default CreateAccount;
